// TanStack Query hooks for the Fatigue Management dashboard.
//
// Every section reads the same /admin/fatigue/summary response through
// useFatigueSummary, so the sections cannot disagree about the headline figure
// and switching section costs no request. The two drill-downs are separate
// queries because they are only fetched when a panel is actually opened.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { qk } from '../../../../../lib/queryKeys';
import { useClientScope } from '../../../../../shared/contexts/ClientScopeContext';
import { fatigueApi } from './fatigueApi';
import type { FatigueQueryParams } from './fatigueApi';
import {
  filterCohorts,
  isFiltered,
  mapCohortDaily,
  mapIndividuals,
  mapSummary,
  buildMetrics,
  rollUpCohortMetrics,
  rollUpCoverage,
  sitesFrom,
} from './fatigueMapper';
import { buildProjection } from './projection';
import type { FatigueFilters, FatigueSummary } from './types';

export const DEFAULT_FILTERS: FatigueFilters = {
  dataQuality: 'full',
  site: 'All sites',
  shiftType: 'All shifts',
};

/** Only the parts of the filter state the API cares about. */
const queryParams = (clientId: string | null, filters: FatigueFilters): FatigueQueryParams => ({
  client_id: clientId,
  reliable_data: filters.dataQuality === 'reliable',
});

export interface FatigueSummaryView extends FatigueSummary {
  /** Cohorts left after the site and shift filters. */
  visibleCohorts: FatigueSummary['cohorts'];
  /** Distinct sites across every cohort, for the site filter's options. */
  sites: string[];
  /** True when the headline figures were recomputed over a filtered subset. */
  narrowed: boolean;
  projection: ReturnType<typeof buildProjection>;
}

export const useFatigueSummary = (filters: FatigueFilters) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.summary(params),
    queryFn: () => fatigueApi.getSummary(params),
    select: mapSummary,
  });

  const data = useMemo<FatigueSummaryView | undefined>(() => {
    if (!query.data) return undefined;
    const summary = query.data;
    const visibleCohorts = filterCohorts(summary.cohorts, filters);
    const narrowed = isFiltered(filters);

    // With no site/shift filter the API's own roll-up is authoritative. With one,
    // the headline is rebuilt from the cohorts still on screen so the cards and
    // the table below them describe the same population.
    const metrics = narrowed ? buildMetrics(rollUpCohortMetrics(visibleCohorts), summary.trend) : summary.metrics;
    const coverage = narrowed ? rollUpCoverage(visibleCohorts) : summary.coverage;

    return {
      ...summary,
      metrics,
      coverage,
      visibleCohorts,
      sites: sitesFrom(summary.cohorts),
      narrowed,
      projection: buildProjection(summary.trend),
    };
  }, [query.data, filters]);

  return { ...query, data };
};

export const useCohortDaily = (cohortId: string | null, filters: FatigueFilters) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.cohortDaily(cohortId, params),
    queryFn: () => fatigueApi.getCohortDaily(cohortId as string, params),
    enabled: Boolean(cohortId),
  });

  return {
    ...query,
    series: query.data ? mapCohortDaily(query.data) : undefined,
    cohort: query.data?.cohort,
    suppressed: query.data?.suppressed ?? false,
  };
};

/**
 * Individual-level detail (FM-8/FM-9). `enabled` is the FM-10 gate — the API
 * enforces the same rule, so this only avoids a request that would 403.
 */
export const useCohortIndividuals = (cohortId: string | null, filters: FatigueFilters, enabled: boolean) => {
  const { effectiveClientId } = useClientScope();
  const params = queryParams(effectiveClientId, filters);

  const query = useQuery({
    queryKey: qk.fatigue.cohortIndividuals(cohortId, params),
    queryFn: () => fatigueApi.getCohortIndividuals(cohortId as string, params),
    enabled: Boolean(cohortId) && enabled,
  });

  return {
    ...query,
    individuals: query.data ? mapIndividuals(query.data) : undefined,
    window: query.data?.window,
  };
};
